import { ModuleId } from '../types';
import { GroupId } from './systemConfig';

/**
 * Dynamic module content mapping.
 * Title and description change based on the student's group (groep 6/7/8).
 */

export interface ModuleDisplayInfo {
  title: string;
  description: string;
}
const MODULE_CONTENT: Record<ModuleId, Record<GroupId, ModuleDisplayInfo>> = {
  Getalbegrip: {
    6: {
      title: 'Grote Getallen & Breuken',
      description: 'Positiewaarde, optellen, aftrekken, breuken vergelijken'
    },
    7: {
      title: 'Breuken, Procenten & Verhoudingen',
      description: 'Procenten berekenen, breuken vereenvoudigen, verhoudingstabellen'
    },
    8: {
      title: 'Procenten, Gemiddelde & Kommagetallen',
      description: 'Procenten terugrekenen, btw, gemiddelde, kommagetallen'
    }
  },
  Metriek: {
    6: {
      title: 'Lengte, Gewicht & Inhoud',
      description: 'Omrekenen mm/cm/m/km, gewicht in gram/kg, liters'
    },
    7: {
      title: 'Oppervlakte & Omtrek',
      description: 'Oppervlakte (m²), omtrek berekenen, metriek stelsel'
    },
    8: {
      title: 'Inhoud, Volume & Schaal',
      description: 'Inhoud (m³/liter), schaalberekening, omrekenen'
    }
  },
  TijdGeld: {
    6: {
      title: 'Klokkijken & Geldrekenen',
      description: 'Analoog en digitaal klokkijken, geld samenstellen, wisselgeld'
    },
    7: {
      title: 'Tijdsduur, Tabellen & Prijzen',
      description: 'Tijdsduur berekenen, tabellen lezen, prijzen vergelijken'
    },
    8: {
      title: 'Grafieken, Snelheid & Verbanden',
      description: 'Grafieken analyseren, km/u berekenen'
    }
  },
  SpellingWW: {
    6: {
      title: 'Stam & Tegenwoordige Tijd',
      description: 'Stam bepalen, ik-vorm, jij-omkering'
    },
    7: {
      title: "Verleden Tijd & 't Kofschip",
      description: "Sterke en zwakke werkwoorden, 't Kofschip, voltooid deelwoord"
    },
    8: {
      title: 'Werkwoordspelling Compleet',
      description: "Voltooid deelwoord, bijvoeglijk gebruikt, d's en t's"
    }
  },
  SpellingRegel: {
    6: {
      title: 'Spellingregels Basis',
      description: 'Lange/korte klanken, v/f- en s/z-wisseling, eind-d/t'
    },
    7: {
      title: 'Zinsontleding & Weetwoorden',
      description: 'Onderwerp, persoonsvorm, leestekens, leenwoorden en trema'
    },
    8: {
      title: 'Grammatica & Ontleden',
      description: 'Lijdend/meewerkend voorwerp, woordsoorten, voorzetsels'
    }
  },
  Taalbegrip: {
    6: {
      title: 'Tekstbegrip & Woordenschat',
      description: 'Vragen over korte teksten, woordbetekenis'
    },
    7: {
      title: 'Begrijpend Lezen',
      description: 'Verwijswoorden, signaalwoorden, doel van de schrijver'
    },
    8: {
      title: 'Taalvaardigheid & Begrippen',
      description: 'Moeilijke woorden, stijlfiguren, taalbegrippen'
    }
  },
  Studievaardig: {
    6: {
      title: 'Informatie Opzoeken',
      description: 'Woordenboek, index en inhoudsopgave gebruiken'
    },
    7: {
      title: "Kaartlezen & Schema's",
      description: "Kaarten, schema's en tabellen interpreteren"
    },
    8: {
      title: 'Studievaardigheden & Informatie',
      description: 'Bronnen beoordelen, informatie ordenen'
    }
  },
  Topo: {
    6: {
      title: 'Topografie: Nederland',
      description: 'Provincies, hoofdsteden en rivieren van Nederland'
    },
    7: {
      title: 'Topografie: Europa',
      description: 'Landen, hoofdsteden en zeeën van Europa'
    },
    8: {
      title: 'Topografie: De Wereld',
      description: 'Werelddelen, oceanen en grote landen'
    }
  }
};

/**
 * Get the dynamic display info for a module based on the student's group.
 * Falls back to the static module data if no mapping exists.
 */
export function getModuleDisplayInfo(moduleId: ModuleId, group: GroupId): ModuleDisplayInfo | null {
  return MODULE_CONTENT[moduleId]?.[group] ?? null;
}

/**
 * Check if a module is a "coming soon" placeholder module.
 */
export const COMING_SOON_MODULES: Set<ModuleId> = new Set(['Studievaardig', 'Topo']);
export function isComingSoon(moduleId: ModuleId): boolean {
  return COMING_SOON_MODULES.has(moduleId);
}